import { useCallback, useMemo } from 'react'
import { CreditApplicationPerson, CreditDocument } from '_gql'

import { useIntl } from '@divvy-web/i18n'

import { useGetCreditApplicationDetails } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'

const formatPersonName = (person: Pick<CreditApplicationPerson, 'firstName' | 'lastName'> | null) =>
  person ? `${person.firstName} ${person.lastName}` : ''

export const useDocumentTypes = ({ salesforceCreditId }: { salesforceCreditId: string }) => {
  const { formatMessage } = useIntl()

  const generatePersonValues = useCallback(
    ({ name, prefix }: { name: string; prefix: string }) => [
      {
        value: CreditDocument[`${prefix}_DRIVERS_LICENSE`],
        label: formatMessage(
          {
            defaultMessage: 'Drivers license for {name}',
            id: 'sputnik.useDocumentTypes__cPOaFl',
          },
          {
            name,
          },
        ),
      },
      {
        value: CreditDocument[`${prefix}_GREEN_CARD`],
        label: formatMessage(
          {
            defaultMessage: 'Green Card for {name}',
            id: 'sputnik.useDocumentTypes__dX1uQy',
          },
          {
            name,
          },
        ),
      },
      {
        value: CreditDocument[`${prefix}_PASSPORT`],
        label: formatMessage(
          {
            defaultMessage: 'Passport for {name}',
            id: 'sputnik.useDocumentTypes__azfrRz',
          },
          {
            name,
          },
        ),
      },
      {
        value: CreditDocument[`${prefix}_PHYSICAL_ADDRESS_VERIFICATION`],
        label: formatMessage(
          {
            defaultMessage: 'Residential address verification for {name}',
            id: 'sputnik.useDocumentTypes__JEjfE1',
          },
          {
            name,
          },
        ),
      },
      {
        value: CreditDocument[`${prefix}_SSN_CARD`],
        label: formatMessage(
          {
            defaultMessage: 'SSN card for {name}',
            id: 'sputnik.useDocumentTypes__FulIEf',
          },
          {
            name,
          },
        ),
      },
      {
        value: CreditDocument[`${prefix}_US_VISA`],
        label: formatMessage(
          {
            defaultMessage: 'US visa for {name}',
            id: 'sputnik.useDocumentTypes__rQpiXT',
          },
          {
            name,
          },
        ),
      },
    ],
    [formatMessage],
  )

  const generateAuthorizedSignerValues = useCallback(
    ({ name }: { name: string }) => generatePersonValues({ name, prefix: 'CP' }),
    [generatePersonValues],
  )

  const generateBeneficialOwnerValues = useCallback(
    ({ name, index }: { name: string; index: number }) => generatePersonValues({ name, prefix: `BO${index}` }),
    [generatePersonValues],
  )

  const getAllDocumentTypes = useCallback(
    ({
      authorizedSignerName,
      beneficialOwner1Name,
      beneficialOwner2Name,
      beneficialOwner3Name,
      beneficialOwner4Name,
    }: {
      authorizedSignerName: string
      beneficialOwner1Name?: string
      beneficialOwner2Name?: string
      beneficialOwner3Name?: string
      beneficialOwner4Name?: string
    }) => [
      {
        value: CreditDocument.BANK_STATEMENT,
        label: formatMessage({
          defaultMessage: 'Bank statement',
          id: 'sputnik.useDocumentTypes__Ga2LEW',
        }),
      },
      {
        value: CreditDocument.BANK_VERIFICATION,
        label: formatMessage({
          defaultMessage: 'Bank verification',
          id: 'sputnik.useDocumentTypes__Mw2CGN',
        }),
      },
      {
        value: CreditDocument.BPAV,
        label: formatMessage({
          defaultMessage: 'Business physical address',
          id: 'sputnik.useDocumentTypes__4IvIJZ',
        }),
      },
      {
        value: CreditDocument.BUSINESS_REGISTRATION,
        label: formatMessage({
          defaultMessage: 'Business registration',
          id: 'sputnik.useDocumentTypes__DfaxEL',
        }),
      },
      {
        value: CreditDocument.BUSINESS_STATUS,
        label: formatMessage({
          defaultMessage: 'Business status',
          id: 'sputnik.useDocumentTypes__oK381n',
        }),
      },
      {
        value: CreditDocument.BUSINESS_TAX_RETURN,
        label: formatMessage({
          defaultMessage: 'Business tax return',
          id: 'sputnik.useDocumentTypes__li91F0',
        }),
      },
      {
        value: CreditDocument.COMPANY_PREPARED,
        label: formatMessage({
          defaultMessage: 'Company prepared',
          id: 'sputnik.useDocumentTypes__gDyM1w',
        }),
      },
      {
        value: CreditDocument.CPA_AUDITED,
        label: formatMessage({
          defaultMessage: 'CPA audited',
          id: 'sputnik.useDocumentTypes__bV44g9',
        }),
      },
      {
        value: CreditDocument.CPA_COMPILED,
        label: formatMessage({
          defaultMessage: 'CPA compiled',
          id: 'sputnik.useDocumentTypes__o/8Cy5',
        }),
      },
      {
        value: CreditDocument.CPA_REVIEW,
        label: formatMessage({
          defaultMessage: 'CPA review',
          id: 'sputnik.useDocumentTypes__Mgo0U1',
        }),
      },
      {
        value: CreditDocument.CRB_PG_AGREEMENT,
        label: formatMessage({
          defaultMessage: 'CRB PG agreement',
          id: 'sputnik.useDocumentTypes__22wKNJ',
        }),
      },
      {
        value: CreditDocument.FINANCIAL_DOCUMENTS,
        label: formatMessage({
          defaultMessage: 'Financial documents',
          id: 'sputnik.useDocumentTypes__Wte4+P',
        }),
      },
      {
        value: CreditDocument.INTERIM_FINANCIALS,
        label: formatMessage({
          defaultMessage: 'Interim financials',
          id: 'sputnik.useDocumentTypes__zaa3fA',
        }),
      },
      {
        value: CreditDocument.NDA,
        label: formatMessage({
          defaultMessage: 'Non-Disclosure agreement',
          id: 'sputnik.useDocumentTypes__e+Wr7f',
        }),
      },
      {
        value: CreditDocument.PERSONAL_TAX_RETURN,
        label: formatMessage({
          defaultMessage: 'Personal tax return',
          id: 'sputnik.useDocumentTypes__bxMyms',
        }),
      },
      {
        value: CreditDocument.PUBLIC_INTERIM_FINANCIALS,
        label: formatMessage({
          defaultMessage: 'Public interim financials',
          id: 'sputnik.useDocumentTypes__GgqHMb',
        }),
      },
      {
        value: CreditDocument.TAX_ID,
        label: formatMessage({
          defaultMessage: 'Tax ID',
          id: 'sputnik.useDocumentTypes__Yl8GBP',
        }),
      },
      {
        value: CreditDocument.OTHER,
        label: formatMessage({
          defaultMessage: 'Other',
          id: 'sputnik.useDocumentTypes__/VnDMl',
        }),
      },
      ...(authorizedSignerName ? generateAuthorizedSignerValues({ name: authorizedSignerName }) : []),
      ...(beneficialOwner1Name
        ? generateBeneficialOwnerValues({
            name: beneficialOwner1Name,
            index: 1,
          })
        : []),
      ...(beneficialOwner2Name
        ? generateBeneficialOwnerValues({
            name: beneficialOwner2Name,
            index: 2,
          })
        : []),
      ...(beneficialOwner3Name
        ? generateBeneficialOwnerValues({
            name: beneficialOwner3Name,
            index: 3,
          })
        : []),
      ...(beneficialOwner4Name
        ? generateBeneficialOwnerValues({
            name: beneficialOwner4Name,
            index: 4,
          })
        : []),
    ],
    [formatMessage, generateAuthorizedSignerValues, generateBeneficialOwnerValues],
  )

  const { data, loading } = useGetCreditApplicationDetails({
    variables: {
      creditApplicationId: salesforceCreditId,
    },
  })

  const options = useMemo(() => {
    if (!data || loading) {
      return []
    }

    const { authorizedSigner, beneficialOwners } = data.creditApplication ?? {
      authorizedSigner: null,
      beneficialOwners: [],
    }

    const presentBeneficialOwners =
      beneficialOwners
        ?.filter((owner) => owner !== null)
        .reduce(
          (acc, curr, index) => ({
            ...acc,
            [`beneficialOwner${index + 1}Name`]: formatPersonName(curr),
          }),
          {},
        ) ?? {}

    return getAllDocumentTypes({
      authorizedSignerName: formatPersonName(authorizedSigner),
      ...presentBeneficialOwners,
    })
  }, [data, getAllDocumentTypes, loading])

  return { options, loading }
}
